import { Button } from "@/shared/components/shadcn/Button";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import type { usePaymentSummary } from "../hooks/paymentQueries";
import { formatPaymentMoney, paymentStatusLabel, type PaymentSummary } from "../types/paymentTypes";

function paidCaption(summary: PaymentSummary) {
  const money = (amount: number) => formatPaymentMoney(amount, summary.currency);
  const parts: string[] = [];
  if (summary.collectedMinor > 0) {
    const methods = summary.cardCollectedMinor > 0 && summary.cashCollectedMinor > 0
      ? ` (${money(summary.cardCollectedMinor)} card, ${money(summary.cashCollectedMinor)} cash)`
      : summary.cashCollectedMinor > 0 ? " in cash" : " by card";
    parts.push(`${money(summary.collectedMinor)} paid${methods}`);
  }
  if (summary.balanceMinor) parts.push(`${money(summary.balanceMinor)} due`);
  return parts.join(" · ");
}

/** Payment value for the appointment Details summary. */
export function AppointmentPaymentStatus({ appointmentId, query }: {
  appointmentId: string;
  query: ReturnType<typeof usePaymentSummary>;
}) {
  const { goToAppointmentPayment } = useAppNavigation();
  const summary = query.data;
  if (query.isPending) return <span className="font-normal text-muted-foreground">Loading…</span>;
  if (query.error || !summary) return <>
    <span className="font-normal text-muted-foreground">Couldn't load payments</span>
    <Button variant="link" className="ml-2 h-auto p-0 text-sm" disabled={query.isFetching} onClick={() => void query.refetch()}>Retry</Button>
  </>;
  const paid = summary.totalMinor !== null && summary.balanceMinor === 0;
  const caption = paidCaption(summary);
  const active = summary.activePayment;
  return <>
    <StatusBadge tone={paid ? "positive" : summary.collectedMinor > 0 ? "pending" : "neutral"}>
      {paid ? "Paid" : summary.collectedMinor > 0 ? "Partially paid" : "Unpaid"}
    </StatusBadge>
    {caption && <span className="mt-1 block text-xs font-normal text-muted-foreground">{caption}</span>}
    {active && <span className="mt-1 block text-xs font-normal text-muted-foreground">
      Card {formatPaymentMoney(active.amountMinor, active.currency)} · {paymentStatusLabel(active)}
      <Button variant="link" className="ml-2 h-auto p-0 text-xs" onClick={() => goToAppointmentPayment(appointmentId, active.id)}>Resume</Button>
    </span>}
  </>;
}
